import { useEffect, useState } from "react";
import { Inbox, Mail, Download, CheckCircle2, Undo2, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiError, downloadCsv } from "@/lib/adminApi";

const NoteEditor = ({ q, onSave }) => {
  const [note, setNote] = useState(q.note || "");
  const [saving, setSaving] = useState(false);
  const dirty = note.trim() !== (q.note || "");

  const save = async () => {
    setSaving(true);
    await onSave(note.trim());
    setSaving(false);
  };

  return (
    <div data-testid="inquiry-note" className="mt-6 border-l-2 border-gold/60 pl-4">
      <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-ink" htmlFor={`note-${q.id}`}>
        <StickyNote size={12} className="text-[#8a6b2f]" /> Private note (only you can see this)
      </label>
      <textarea id={`note-${q.id}`} rows={2} maxLength={2000} data-testid="inquiry-note-input" value={note} onChange={(e) => setNote(e.target.value)}
        placeholder="e.g. Called back Tuesday, sending a quote for monthly bookkeeping."
        className="mt-2 w-full bg-transparent border-b border-[#C9BFA8] focus:border-[#8a6b2f] outline-none py-2 text-navy text-sm placeholder:text-ink-muted/60 resize-none transition-colors" />
      <button onClick={save} disabled={!dirty || saving} data-testid="inquiry-note-save-btn"
        className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[#8a6b2f] text-[#8a6b2f] hover:bg-[#8a6b2f] hover:text-cream transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[#8a6b2f]">
        {saving ? "Saving…" : "Save note"}
      </button>
    </div>
  );
};

const FILTERS = [
  { key: "open", label: "Open" },
  { key: "handled", label: "Handled" },
  { key: "all", label: "All" },
];
const PACKAGE_FILTERS = [
  { key: "any", label: "Any package" },
  { key: "essentials", label: "Essentials" },
  { key: "standard", label: "Standard" },
  { key: "legacy", label: "Former tiers" },
  { key: "none", label: "No package" },
];

// New inquiries store `package`; older ones only carried the label inside service_type.
const tierOf = (q) => {
  const text = `${q.package || ""} ${q.service_type || ""}`.toLowerCase();
  const current = ["essentials", "standard"].find((t) => text.includes(`${t} package`));
  if (current) return current;
  // Inquiries from before the pricing change named Starter / Plus (or Standard at the old price).
  return ["starter", "plus"].some((t) => text.includes(`${t} package`)) ? "legacy" : "none";
};
const TIER_TAG = { essentials: "Essentials", standard: "Standard", legacy: "Former tier" };

const fmt = (iso) =>
  new Date(iso).toLocaleString("en-US", { timeZone: "America/Phoenix", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const InquiryRow = ({ q, open, onToggle, onHandled, onNote }) => (
  <li data-testid="inquiry-item" data-handled={q.handled ? "true" : "false"}>
    <button onClick={onToggle} aria-expanded={open}
      className="w-full text-left py-4 grid sm:grid-cols-12 gap-2 sm:gap-4 items-baseline hover:bg-cream transition-colors px-2 -mx-2">
      <span className="sm:col-span-3 flex items-center gap-2 text-navy font-medium">
        {q.handled && <CheckCircle2 size={14} className="text-[#8a6b2f] shrink-0" />}
        {q.full_name}
        {q.note && <StickyNote size={12} className="text-[#8a6b2f] shrink-0" data-testid="inquiry-note-indicator" />}
      </span>
      <span className="sm:col-span-3 text-ink-muted text-sm truncate">{q.company || "—"}</span>
      <span className="sm:col-span-3 font-mono text-[10px] uppercase tracking-[0.18em] text-[#8a6b2f]">
        {TIER_TAG[tierOf(q)] && <span data-testid="inquiry-package-tag" className="inline-block mr-2 px-2 py-0.5 border border-[#8a6b2f]/50 text-navy">{TIER_TAG[tierOf(q)]}</span>}
        {q.service_type}
      </span>
      <span className="sm:col-span-3 text-ink-muted text-xs sm:text-right">{fmt(q.created_at)}</span>
    </button>
    {open && (
      <div data-testid="inquiry-detail" className="pb-6 px-2 -mx-2">
        <p className="text-navy leading-relaxed whitespace-pre-wrap">{q.message}</p>
        <div className="flex flex-wrap items-center gap-6 mt-5">
          <a href={`mailto:${q.email}?subject=Re: your Oakford Ledger inquiry`} data-testid="inquiry-reply-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-navy link-underline">
            <Mail size={13} className="text-[#8a6b2f]" /> Reply to {q.email}
          </a>
          <button onClick={() => onHandled(!q.handled)} data-testid="inquiry-handled-toggle"
            className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${q.handled ? "border-[#C9BFA8] text-ink-muted hover:text-navy" : "bg-navy text-parchment border-navy hover:bg-navy-2"}`}>
            {q.handled ? <><Undo2 size={12} /> Reopen</> : <><CheckCircle2 size={12} /> Mark as handled</>}
          </button>
          {q.handled && q.handled_at && <span className="text-ink-muted text-xs">Handled {fmt(q.handled_at)}</span>}
        </div>
        <NoteEditor key={q.note} q={q} onSave={onNote} />
      </div>
    )}
  </li>
);

export const InquiryInbox = ({ onAuthError }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("open");
  const [pkgFilter, setPkgFilter] = useState("any");
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    adminApi.get("/admin/inquiries").then((r) => setItems(r.data)).catch((e) => e?.response?.status === 401 && onAuthError());
  }, [onAuthError]);

  const patch = async (q, body, okMsg) => {
    try {
      const { data } = await adminApi.patch(`/admin/inquiries/${q.id}`, body);
      setItems((list) => list.map((x) => (x.id === q.id ? data : x)));
      toast.success(okMsg);
    } catch (err) {
      if (err?.response?.status === 401) onAuthError(); else toast.error(apiError(err));
    }
  };
  const setHandled = (q, handled) => patch(q, { handled }, handled ? `Marked ${q.full_name} as handled.` : `Reopened ${q.full_name}.`);
  const setNote = (q, note) => patch(q, { note }, note ? "Note saved." : "Note cleared.");

  const openCount = items?.filter((q) => !q.handled).length ?? 0;
  const visible = items?.filter((q) => (filter === "all" || (filter === "handled") === Boolean(q.handled)) && (pkgFilter === "any" || tierOf(q) === pkgFilter)) ?? [];

  return (
    <section data-testid="admin-inquiry-inbox" className="bg-cream-ivory border border-[#D9CFB8] p-8 lg:p-10">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8a6b2f] mb-2">Inquiry inbox</p>
          <h2 className="font-serif text-3xl text-navy">Contact form submissions</h2>
        </div>
        <div className="text-right">
          <span data-testid="inquiry-open-count" className="font-serif text-4xl text-navy">{items ? openCount : "—"}</span>
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted">open · <span data-testid="inquiry-count">{items ? items.length : "—"}</span> total</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-8">
        {FILTERS.map((f) => (
          <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`inquiry-filter-${f.key}`}
            className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${filter === f.key ? "bg-navy text-parchment border-navy" : "border-[#C9BFA8] text-ink-muted hover:text-navy"}`}>
            {f.label}
          </button>
        ))}
        <span className="max-sm:hidden block w-px self-stretch bg-[#D9CFB8] mx-2" aria-hidden="true" />
        {PACKAGE_FILTERS.map((f) => {
          const n = items?.filter((q) => f.key === "any" || tierOf(q) === f.key).length ?? 0;
          return (
            <button key={f.key} onClick={() => setPkgFilter(f.key)} data-testid={`inquiry-package-filter-${f.key}`}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border transition-colors ${pkgFilter === f.key ? "bg-[#8a6b2f] text-cream border-[#8a6b2f]" : "border-[#C9BFA8] text-ink-muted hover:text-navy"}`}>
              {f.label} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
        <button data-testid="inquiries-export-btn"
          onClick={() => downloadCsv("/admin/export/inquiries.csv", "oakford-inquiries.csv").catch(() => toast.error("Export failed."))}
          className="ml-auto inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[#8a6b2f] text-[#8a6b2f] hover:bg-[#8a6b2f] hover:text-cream transition-colors">
          <Download size={12} /> CSV
        </button>
      </div>

      {items && visible.length === 0 && (
        <div data-testid="inquiry-empty" className="flex items-center gap-3 mt-8 text-ink-muted text-sm">
          <Inbox size={16} /> {filter === "open" ? "All caught up — no open inquiries." : "Nothing here yet."}
        </div>
      )}

      {visible.length > 0 && (
        <ul data-testid="inquiry-list" className="mt-6 divide-y divide-[#D9CFB8]">
          {visible.map((q) => (
            <InquiryRow key={q.id} q={q} open={openId === q.id}
              onToggle={() => setOpenId(openId === q.id ? null : q.id)}
              onHandled={(h) => setHandled(q, h)}
              onNote={(n) => setNote(q, n)} />
          ))}
        </ul>
      )}
    </section>
  );
};
